import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AuthCallback - Handles Emergent Google OAuth callback
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 * 
 * Flow:
 * 1. User clicks Google login -> Redirects to Emergent auth
 * 2. After Google auth, user lands at {origin}/#session_id={session_id}
 * 3. This component extracts session_id, exchanges it for user data
 * 4. Stores JWT token, navigates to dashboard
 */
const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithGoogleSession } = useAuth();

  useEffect(() => {
    // Prevent double processing (React StrictMode)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuthCallback = async () => {
      try {
        // Extract session_id from URL fragment (hash)
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('No session_id in URL');
          navigate('/auth', { replace: true });
          return;
        }

        // Exchange session_id for user data via backend
        const response = await fetch(`${API}/auth/google/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Authentication failed');
        }

        const data = await response.json();
        
        // Store the JWT token
        await loginWithGoogleSession(data.access_token, data.user);

        // Clear the hash from URL and navigate to dashboard
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/dashboard', { replace: true, state: { user: data.user } });
        
      } catch (error) {
        console.error('Google auth callback error:', error);
        navigate('/auth', { 
          replace: true, 
          state: { error: error.message || 'Failed to sign in with Google' }
        });
      }
    };

    processAuthCallback();
  }, [navigate, loginWithGoogleSession]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] via-[#0f2847] to-[#0a1628] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-[#5A9BD5] animate-spin mx-auto mb-4" />
        <p className="text-white text-lg">Completing sign in...</p>
        <p className="text-gray-400 text-sm mt-2">Please wait</p>
      </div>
    </div>
  );
};

export default AuthCallback;
